import { CSSProperties, useCallback, useMemo, useRef, useState } from 'react';
import useBuildVariantProps from '../../../modules/theme/useBuildVariantProps';
import { getPlacement, TOOLTIP_VARIANT_MAP } from './Tooltip.constants';
import { TooltipProps } from './Tooltip.types';

const usePresenter = (props: TooltipProps) => {
  const {
    direction = 'Top',
    alignment = 'Center',
    action = 'Hover',
    isVisible = false,
    style = 'Default',
    target,
  } = props;

  const [visible, setVisible] = useState(isVisible);
  const targetRef = useRef<HTMLDivElement>(null);
  const cursorRef = useRef<HTMLDivElement>(null);
  const updateRef = useRef<(() => void) | null>(null);

  const showTooltip = () => {
    setVisible(true);
  };

  const hideTooltip = () => {
    setVisible(false);
  };

  const toggleTooltip = () => {
    setVisible((prev) => !prev);
  };

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (cursorRef.current) {
      cursorRef.current.style.left = `${e.clientX}px`;
      cursorRef.current.style.top = `${e.clientY}px`;
      updateRef.current?.();
    }
  }, []);

  const placement = getPlacement(direction, alignment);

  const variantKey = `${direction}-${alignment}` as keyof typeof TOOLTIP_VARIANT_MAP;
  const baseVariant = TOOLTIP_VARIANT_MAP[variantKey];

  const variantProps = useBuildVariantProps(
    'tooltip',
    { baseVariant, style },
    {},
  );

  // To show the arrow pointed in the middle of the target
  const arrowOffset = useMemo(() => {
    if (targetRef.current) {
      let offset = 0;
      switch (direction) {
        case 'Top':
          offset = targetRef.current.offsetWidth / 2;
          break;
        case 'Bottom':
          offset = targetRef.current.offsetWidth / 2;
          break;
        case 'Left':
          offset = targetRef.current.offsetHeight / 2;
          break;
        case 'Right':
          offset = targetRef.current.offsetHeight / 2;
          break;
      }
      return offset;
    }
    return undefined;
  }, [direction, targetRef.current]);

  const arrowOffsetStyle: CSSProperties | undefined = useMemo(() => {
    if (arrowOffset) {
      return {
        '--arrow-offset': `${arrowOffset}px`,
      } as CSSProperties;
    }
    return undefined;
  }, [arrowOffset, direction, alignment]);

  return {
    direction,
    alignment,
    action,
    target,
    showTooltip,
    hideTooltip,
    toggleTooltip,
    targetRef,
    cursorRef,
    updateRef,
    handleMouseMove,
    variantProps,
    style,
    visible,
    placement,
    arrowOffsetStyle,
  };
};

export default usePresenter;
